import { useFieldIfAvailable } from "../../../core/useFieldIfAvailable";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Button, FormGroup, Input, Text, tombac } from 'tombac';
import { CancelIcon } from 'tombac-icons';
import { SharedLimitWarning } from "./limitEditorElements";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function MortonIdsLimitEditor({
  className,
  isSharedWithOther = false,
  label,
  limit = null,
  onChange,
  schema
}) {
  const [field,, helpers] = useFieldIfAvailable(schema.name);

  // Integrate with Formik when available
  if (field) {
    limit = field.value ?? null;
    onChange = helpers.setValue;
  }

  // Local input state for raw text entry
  const [input, setInput] = useState('');

  // Parse integers from user input (supports commas, spaces, semicolons, newlines, etc.)
  const parseIds = useCallback(text => {
    const matches = text.match(/\d+/g);
    if (!matches) return [];
    return matches.map(s => Number(s)).filter(n => Number.isFinite(n));
  }, []);
  const parsedCount = useMemo(() => parseIds(input).length, [input, parseIds]);
  const handleInputChange = useCallback(event => {
    setInput(event.target.value);
  }, []);
  const handleAdd = useCallback(() => {
    const ids = parseIds(input);
    if (!ids.length) return;
    const base = Array.isArray(limit) ? limit : [];
    const merged = [...new Set([...base, ...ids])].sort((a, b) => a - b);
    onChange?.(merged);
    setInput('');
  }, [input, parseIds, limit, onChange]);
  const handleRemove = useCallback(id => {
    const base = Array.isArray(limit) ? limit : [];
    const next = base.filter(x => x !== id).sort((a, b) => a - b);
    if (next.length === 0) {
      onChange?.(null);
    } else {
      onChange?.(next);
    }
  }, [limit, onChange]);

  // Effective list to display
  const idsToDisplay = Array.isArray(limit) ? limit : [];

  // Scroll shadows (appear only when scrolling is possible in a direction)
  const scrollRef = useRef(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  const updateShadows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = el;
    setShowTopShadow(scrollTop > 0);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 1);
  }, []);
  useEffect(() => {
    updateShadows();
  }, [idsToDisplay.length, updateShadows]);
  return /*#__PURE__*/_jsxs(Box, {
    className: className,
    $width: "100%",
    "data-sentry-element": "Box",
    "data-sentry-component": "MortonIdsLimitEditor",
    "data-sentry-source-file": "MortonIdsLimitEditor.tsx",
    children: [/*#__PURE__*/_jsx(FormGroup, {
      label: label,
      hint: "Enter IDs separated by commas, spaces, semicolons, or new lines.",
      $width: "100%",
      "data-sentry-element": "FormGroup",
      "data-sentry-source-file": "MortonIdsLimitEditor.tsx",
      "data-sentry-element": "FormGroup",
      children: /*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "MortonIdsLimitEditor.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(Input, {
          $flex: "auto",
          disabled: isSharedWithOther,
          id: `input-${schema?.name}-morton`,
          name: field?.name,
          onBlur: field?.onBlur,
          onChange: handleInputChange,
          placeholder: "",
          type: "text",
          value: input,
          $width: "100%",
          "data-sentry-element": "Input",
          "data-sentry-source-file": "MortonIdsLimitEditor.tsx",
          "data-sentry-element": "Input"
        }), isSharedWithOther && /*#__PURE__*/_jsx(SharedLimitWarning, {})]
      })
    }), schema.customizable ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Box, {
        $display: "flex",
        $alignItems: "center",
        $justifyContent: "space-between",
        $mt: "1sp",
        children: [/*#__PURE__*/_jsx(Box, {
          $display: "flex",
          $alignItems: "center",
          children: !isSharedWithOther && /*#__PURE__*/_jsx(Button, {
            onClick: handleAdd,
            type: "button",
            disabled: parsedCount === 0,
            variant: "primary",
            size: "s",
            children: parsedCount > 1 ? `Add ${parsedCount} IDs` : 'Add IDs'
          })
        }), !isSharedWithOther && idsToDisplay.length > 0 && /*#__PURE__*/_jsx(Text, {
          $color: tombac.color('danger'),
          as: "button",
          onClick: () => onChange?.(null),
          variant: "interactive",
          children: "Delete all"
        })]
      }), idsToDisplay.length > 0 ? /*#__PURE__*/_jsxs(ScrollArea, {
        ref: scrollRef,
        onScroll: updateShadows,
        children: [showTopShadow && /*#__PURE__*/_jsx(TopBar, {}), /*#__PURE__*/_jsx(OptionList, {
          children: idsToDisplay.map(id => /*#__PURE__*/_jsxs(Box, {
            as: "li",
            $display: "flex",
            $alignItems: "center",
            $justifyContent: "space-between",
            $pb: "0.25sp",
            $pt: "0.25sp",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "span",
              children: id
            }), !isSharedWithOther && /*#__PURE__*/_jsx(Button, {
              onClick: () => handleRemove(id),
              shape: "circle",
              type: "button",
              variant: "flat",
              size: "xs",
              "aria-label": `Remove ${id}`,
              children: /*#__PURE__*/_jsx(CancelIcon, {})
            })]
          }, id))
        }), showBottomShadow && /*#__PURE__*/_jsx(BottomBar, {})]
      }) : null]
    }) : idsToDisplay.length > 0 ? /*#__PURE__*/_jsx(Text, {
      as: "div",
      children: idsToDisplay.join(', ')
    }) : null]
  });
}
const ScrollArea = styled.div.withConfig({
  displayName: "MortonIdsLimitEditor__ScrollArea"
})(["position:relative;margin-top:", ";max-height:", ";overflow-y:auto;border-top:", ";border-bottom:", ";"], tombac.space(1), tombac.unit(160), tombac.parse('1u solid --neutral-300'), tombac.parse('1u solid --neutral-300'));
const TopBar = styled.div.withConfig({
  displayName: "MortonIdsLimitEditor__TopBar"
})(["position:sticky;top:0;height:", ";background:radial-gradient( ellipse at top,rgba(0,0,0,0.14),rgba(0,0,0,0) 70% );pointer-events:none;z-index:1;"], tombac.unit(6));
const BottomBar = styled.div.withConfig({
  displayName: "MortonIdsLimitEditor__BottomBar"
})(["position:sticky;bottom:0;height:", ";background:radial-gradient( ellipse at bottom,rgba(0,0,0,0.14),rgba(0,0,0,0) 70% );pointer-events:none;z-index:1;"], tombac.unit(6));
const OptionList = styled.ul.withConfig({
  displayName: "MortonIdsLimitEditor__OptionList"
})(["list-style:none;padding:0;margin:", " 0;"], tombac.space(0.25));